import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, KeyRound, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

export default function DepartmentCodeGate({ onUnlock }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim()) { setError("Enter your department code"); return; }
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/department-codes/verify", { code: code.trim() });
      toast.success(`Verified — ${data.department}`);
      onUnlock(code.trim().toUpperCase(), data);
    } catch (err) {
      setError("That code is not recognised. Please ask your supervisor for the correct one.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-md mx-auto px-4 sm:px-6 py-8 sm:py-16" data-testid="dept-code-gate">
      <Link to="/" className="text-sm text-muted-foreground inline-flex items-center gap-1.5 mb-4 hover:text-foreground">
        <ChevronLeft className="w-4 h-4"/> Back
      </Link>
      <Card>
        <CardContent className="p-8">
          <div className="w-12 h-12 rounded-lg bg-accent text-primary mx-auto flex items-center justify-center mb-4">
            <KeyRound className="w-5 h-5" strokeWidth={1.75}/>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-center">Department code required</h1>
          <p className="text-sm text-muted-foreground mt-2 mb-6 text-center">
            Enter the code given to you by your supervisor to access the absence notification form.
          </p>
          <form onSubmit={submit} className="space-y-4" data-testid="dept-code-form">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">Department code</Label>
              <Input
                data-testid="dept-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. PNC2"
                autoComplete="off"
                autoFocus
                className="font-mono tracking-wider text-center text-lg h-12"
              />
            </div>
            {error && (
              <div data-testid="dept-code-error" className="status-rejected rounded-md px-3 py-2 text-sm flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0"/>
                <span>{error}</span>
              </div>
            )}
            <Button data-testid="dept-code-submit" type="submit" disabled={busy} className="w-full h-11 gap-2">
              {busy ? "Verifying…" : "Continue to request form"}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center pt-1">
              Codes are case-insensitive. Lost it? Ask your supervisor.
            </p>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
